import { useState, useEffect } from 'react';
import type { QuestTemplateCheckInFirestore } from '../../services/questTypes';
import { QuestFormFields } from './QuestFormFields';
import { useQuestForm } from './useQuestForm';
import { QuestFormActions } from './QuestFormActions';
import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import ErrorDisplay from '../../../../components/ErrorDisplay';

interface CheckInSpecificFormData {
    aiPromptGuidance: string;
}

interface CheckInQuestFormProps {
    questTemplate?: QuestTemplateCheckInFirestore;
    initialOrder: number;
    onSave: (questTemplate: QuestTemplateCheckInFirestore) => void;
    onSaveAndContinue?: (questTemplate: QuestTemplateCheckInFirestore) => void;
    onCancel: () => void;
}

export const CheckInQuestForm = ({ questTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: CheckInQuestFormProps) => {
    // Use the common quest form hook
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue
    } = useQuestForm({
        questTemplate,
        initialOrder,
        defaultProgressType: 'fixed'
    });

    // Check-in-specific form data
    const [specificFormData, setSpecificFormData] = useState<CheckInSpecificFormData>({
        aiPromptGuidance: '',
    });

    // Initialize check-in-specific data
    useEffect(() => {
        if (questTemplate && questTemplate.id) {
            setSpecificFormData({
                aiPromptGuidance: questTemplate.aiPromptGuidance,
            });
        }
    }, [questTemplate]);

    const handleSpecificChange = (field: string, value: string) => {
        setSpecificFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | boolean) => {
        // Check if it's a common field or specific field
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | boolean);
        } else {
            handleSpecificChange(field, value as string);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.aiPromptGuidance.trim()) {
            return 'AI Prompt Guidance is required';
        }
        return null;
    };

    const createQuestTemplate = (baseData: Partial<QuestTemplateCheckInFirestore>): QuestTemplateCheckInFirestore => {
        const questTemplateData: QuestTemplateCheckInFirestore = {
            ...baseData,
            type: 'check-in',
            maxProgress: 1,
            aiPromptGuidance: specificFormData.aiPromptGuidance,
        } as QuestTemplateCheckInFirestore;

        return questTemplateData;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createQuestTemplate, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createQuestTemplate, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormFields
                formData={commonFormData}
                questType="check-in"
                disableProgressType={false}
                onChange={handleChange}
            />

            <QuestFormSectionDivider title="Check-in-specific fields" />

            {/* Check-in-specific fields */}
            <div>
                <label htmlFor="aiPromptGuidance" className="block text-sm font-medium text-gray-700 mb-1">
                    AI Prompt Guidance *
                </label>
                <textarea
                    id="aiPromptGuidance"
                    value={specificFormData.aiPromptGuidance}
                    onChange={(e) => handleChange('aiPromptGuidance', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter AI prompt guidance"
                    rows={3}
                    required
                />
                <p className="text-xs text-gray-500 mt-1">
                    This is the prompt that will be used to supplement the base quest prompt.
                </p>
            </div>

            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!questTemplate?.id}
            />
        </form>
    );
};

